import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { dateToString } from "@shared/utils/DateUtils";
import {
  CellValueChangedEvent,
  RowData,
} from "@shared/components/GridV2/SnGridTypes";
import SnResizeBar from "@shared/components/SnResizeBar";

interface Props {
  Menuprops: menuInfo;
}

const SN_SCT_BAS_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const defaultForm = {
          cd_biz: userInfo.cd_biz,
          cd_emp: userInfo.cd_emp,
          nm_emp: userInfo.nm_emp,
        };
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData({ ...rtn.formData, ...defaultForm });
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();

    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_sct_bas_reg_002_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const sctBomHChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (sctBomHChanges.length > 0) return false;

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const newRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        cd_cust: searchInfo.cd_cust,
        nm_cust: searchInfo.nm_cust,
        um: 0,
        yn_use: "Y",
        cd_exch: "000",
        qt_unit: 0,
      };
      gridRefs.current["grid1"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onAddRow = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      gridRefs.current["grid2"].current?.addRow({});
    } catch (error: any) {}
  };

  const onBeforeAddRowL = (): any => {
    try {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        no_line: maxNoLine + 1,
        cd_cust: focusedNodeH.data?.cd_cust,
        fg_matl: "1",
        cd_item: focusedNodeH.data?.cd_item,
        cd_exch: focusedNodeH.data?.cd_exch,
        rt_loss: 0,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid2"].current.deleteAllRows();
    gridRefs.current["grid1"].current.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;
    gridRefs.current["grid2"].current?.deleteSelectedRows();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const sctBomHChanges = gridRefs.current["grid1"].current.getChange();
      const sctBomLChanges = gridRefs.current["grid2"].current.getChange();
      const headerInserts = sctBomHChanges.insert ?? [];
      const lineInserts = sctBomLChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        sctBomHChanges.modifiedRowcount === 0 &&
        sctBomLChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const saveData = {
        sct_bomh: sctBomHChanges,
        sct_boml: sctBomLChanges,
      };
      const ok = await fnSave("SN_SCT_BAS_REG_002_SAVE_01", saveData);
      if (!ok) return false;

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
    }
  };

  const onCellValueChanged2 = (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    const qtJust = parseFloat(rowData.qt_just) || 0;
    const rtLoss = parseFloat(rowData.rt_loss) || 0;

    if (colId === "qt_just" || colId === "rt_loss") {
      rowData.qt_need = qtJust * (1 + rtLoss / 100);
      gridRefs.current["grid2"].current.applyTransaction({ update: [rowData] });
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChanged,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChanged2,
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "BOM정보" }}
            events={grids.grid1.events}
            filter={{
              refs: [gridRefs.current["grid2"]],
              columns: ["cd_cust", "cd_item", "cd_exch"],
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "single", title: "자재정보" }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_SCT_BAS_REG_002;
